import { useEffect } from 'react';
import { getGuias } from '../lib/guiasAgenciasStorage';
import { getCidades, getPublishedExperiences } from '../lib/experienciasStorage';

const MESSAGE_TYPE = 'naviex-guia-export';

/** Formato de cidade para o guia (com lat/lng opcional para GPS) */
export interface CidadeExport {
  id: string;
  cidade: string;
  estadoProvincia: string;
  regioes: string[];
  lat?: number;
  lng?: number;
}

/**
 * Página pública usada pelo guia em iframe para obter dados do guia e cidades
 * quando guia e admin rodam em origens diferentes (ex.: localhost:3001 vs 3002).
 * Query: ?email=xxx retorna o guia com esse email e lista de cidades.
 * Envia os dados via postMessage para window.parent.
 */
export default function GuiaExportPage() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const email = (params.get('email') || '').trim().toLowerCase();
      const guias = getGuias();
      const cidades = getCidades();
      const guia = email ? guias.find((g) => (g.email || '').toLowerCase() === email) : null;
      const cidade = guia?.baseCidadeId
        ? cidades.find((c) => c.id === guia.baseCidadeId)
        : null;
      const baseCidadeNome = cidade ? [cidade.cidade, cidade.estadoProvincia].filter(Boolean).join(', ') : '';
      const coordsFallback: Record<string, [number, number]> = {
        'Alto Paraíso de Goiás': [-14.1304, -47.5202],
        'São Jorge': [-14.1667, -47.9833],
        'Cavalcante': [-13.7972, -47.4564],
      };
      const cidadesExport: CidadeExport[] = cidades.map((c) => {
        const stored = c as { lat?: number; lng?: number };
        let lat = stored.lat;
        let lng = stored.lng;
        if (lat == null || lng == null) {
          const fallback = coordsFallback[`${c.cidade}`.trim()] || coordsFallback[`${c.cidade}, ${c.estadoProvincia}`.trim()];
          if (fallback) [lat, lng] = fallback;
        }
        return {
          id: c.id,
          cidade: c.cidade,
          estadoProvincia: c.estadoProvincia || '',
          regioes: c.regioes || [],
          lat,
          lng,
        };
      });
      const experiencias = getPublishedExperiences();
      const payload = guia
        ? {
            id: guia.id,
            baseCidadeId: guia.baseCidadeId,
            baseCidadeNome,
            nome: guia.nome,
            cpf: guia.cpf,
            email: guia.email,
            telefone: guia.telefone,
            chavePix: guia.chavePix,
            baseCidadePendenteAprovacao: guia.baseCidadePendenteAprovacao,
            imagemPerfil: guia.imagemPerfil,
            certificados: guia.certificados,
            agenciaId: guia.agenciaId ?? '',
            resumoPerfil: guia.resumoPerfil ?? '',
            documentoIdentidadeFornecedor: guia.documentoIdentidadeFornecedor,
            documentoIdentificacao: guia.documentoIdentificacao,
            certificadoAnexo: guia.certificadoAnexo,
            certificadoDataConclusao: guia.certificadoDataConclusao,
            fotosTrabalhoGuia: guia.fotosTrabalhoGuia,
            fotosTrabalhoTerapeuta: guia.fotosTrabalhoTerapeuta,
            perfis: guia.perfis,
          }
        : null;
      if (window.parent !== window) {
        window.parent.postMessage({ type: MESSAGE_TYPE, guia: payload, cidades: cidadesExport, experiencias }, '*');
      }
    } catch (err) {
      if (window.parent !== window) {
        window.parent.postMessage({ type: MESSAGE_TYPE, guia: null, cidades: [], experiencias: [], error: String(err) }, '*');
      }
    }
  }, []);

  return (
    <div className="p-4 text-sm text-gray-500">
      Dados do guia enviados.
    </div>
  );
}

export { MESSAGE_TYPE };
